import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import promotionApi from '../../services/promotionApi';
import API_BASE_URL from '../../config/api';

/**
 * Gestionnaire pour afficher plusieurs promotions en bande en haut (côte à côte)
 */
const PromotionsBannerManager = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isClosed, setIsClosed] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState(80);

  useEffect(() => {
    fetchBannerPromotions();
    
    // Calculer la hauteur réelle de la navbar
    const updateNavbarHeight = () => {
      const navbar = document.querySelector('nav') || document.querySelector('[class*="z-50"]');
      if (navbar) {
        setNavbarHeight(navbar.offsetHeight);
      }
    };
    
    updateNavbarHeight();
    window.addEventListener('resize', updateNavbarHeight);
    
    return () => window.removeEventListener('resize', updateNavbarHeight);
  }, []);

  const fetchBannerPromotions = async () => {
    try {
      const data = await promotionApi.getPublicPromotions({
        display_format: 'top_banner',
        status: 'active',
        limit: 10
      });
      setPromotions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement promotions bandeau:', error);
      setPromotions([]);
    }
  };

  const handleClose = () => {
    setIsClosed(true);
  };

  useEffect(() => {
    if (promotions.length <= 1 || isClosed) return;
    const duration = 5 * 1000;
    const timer = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % promotions.length);
    }, duration);
    return () => clearTimeout(timer);
  }, [currentIndex, promotions.length, isClosed]);

  const handleOrder = (promotion) => {
    if (promotion.restaurant_id) navigate(`/restaurant/${promotion.restaurant_id}`);
    else navigate('/');
  };

  if (promotions.length === 0 || isClosed) return null;

  const currentPromo = promotions[currentIndex];
  const normalizeMediaUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    const base = (API_BASE_URL || '').replace(/\/$/, '');
    return trimmed.startsWith('/') ? `${base}${trimmed}` : `${base}/${trimmed}`;
  };

  const imageUrl = normalizeMediaUrl(currentPromo.media_url || currentPromo.image_url);
  const shortDesc = currentPromo.short_description || currentPromo.description;
  const ctaText = currentPromo.cta_label || currentPromo.cta_text;

  return (
    <div
      className="fixed left-0 right-0 z-40 shadow-lg"
      style={{ 
        backgroundColor: '#FFFFFF',
        top: `${navbarHeight}px`
      }}
    >
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          <div
            className="flex items-center space-x-3 flex-1 min-w-0 w-full"
            style={{ 
              backgroundColor: currentPromo.background_color || '#FFFFFF',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}
          >
            {imageUrl && (
              <img
                src={imageUrl}
                alt={currentPromo.title}
                className="h-12 w-16 object-cover rounded flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                {currentPromo.label && (
                  <span className={`px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 ${
                    currentPromo.label === 'offre_du_jour' ? 'bg-yellow-500 text-white' : 'bg-green-500 text-white'
                  }`}>
                    {currentPromo.label === 'offre_du_jour' ? t('promotions.dealOfTheDay') : t('promotions.new')}
                  </span>
                )}
                <h3 className="font-bold text-sm text-gray-900 truncate">{currentPromo.title}</h3>
              </div>
              {shortDesc && (
                <p className="text-xs text-gray-900 font-normal line-clamp-1 truncate">{shortDesc}</p>
              )}
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <span className="text-lg font-bold text-green-600">
                {currentPromo.final_price != null ? Number(currentPromo.final_price).toFixed(2) : '—'}€
              </span>
              {currentPromo.original_price != null && currentPromo.final_price != null && currentPromo.original_price > currentPromo.final_price && (
                <span className="text-xs text-gray-900 font-normal line-through">
                  {Number(currentPromo.original_price).toFixed(2)}€
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleOrder(currentPromo)}
              className="bg-green-600 text-white py-1.5 px-3 rounded-lg hover:bg-green-700 transition-colors flex items-center text-xs flex-shrink-0"
            >
              {ctaText || t('promotions.discover')}
            </button>

            {/* Bouton fermer */}
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
              aria-label={t('promotions.close')}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionsBannerManager;
